import { OnDestroy, OnInit, Directive, inject } from '@angular/core';
import { AuthService } from '../../services';
import { ActivatedRoute, Router } from '@angular/router';
import { PageFilterStateService } from '../../services/page-filter-state.service';

@Directive()
export abstract class BasePageComponent implements OnInit, OnDestroy {
  protected route = inject(ActivatedRoute);
  protected router = inject(Router);
  protected authService = inject(AuthService);
  protected pageFilterStateService = inject(PageFilterStateService);

  protected isPageInitialized: boolean = false;
  private refreshIntervalId: any;

  protected abstract onRefresh(): void;
  protected abstract shouldRefresh(): boolean;

  /**
   * Hook method that returns the current filters used by the subclass.
   *
   * Subclasses should override this method to return the filter object
   * that will be stored in the page filter state.
   *
   * @returns {Object} The current filter state.
   */
  protected abstract getFilters(): any;

  /**
   * Hook method that applies the given page filter state to the component.
   * 
   * Subclasses must implement this method to define how the page filter
   * properties (e.g., dateFilter, accountFilter, searchTerm) are applied
   * to the component state.
   *
   * @param pageFilter - The saved page filter state to apply.
   */
  protected abstract applyPageFilterState(pageFilter: any): void;

  ngOnInit(): void {
    this.restorePageFilter();
    this.initPageRefresh();
    this.isPageInitialized = true;
  }

  ngOnDestroy(): void {
    if (this.refreshIntervalId) {
      clearInterval(this.refreshIntervalId);
    }
  }

  /**
   * Stores the current filter state in the PageFilterStateService
   * and updates the URL query parameters to indicate that filters
   * are applied. This ensures that filters can be restored after
   * a page refresh.
   */
  protected updatePageFilterState(): void {
    this.pageFilterStateService.clearFilter();
    this.pageFilterStateService.storeFilter(this.getFilters());

    // set query params to load filters when refreshing the page
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { filtered: true },
      queryParamsHandling: ''
    });
  }

  /**
   * Restores the page filter state from the PageFilterStateService
   * if filters have not already been restored and filtering is active.
   * 
   * Subclasses can override the `applyPageFilterState` method to customize
   * how the filters are applied to the component state.
   */
  private restorePageFilter(): void {

    const fromMap = this.route.snapshot.queryParamMap.get('fromMap')?.toLowerCase() == 'true';
    const isFiltered = this.route.snapshot.queryParamMap.get('filtered')?.toLowerCase() == 'true';

    if ((!fromMap && !isFiltered) || this.isPageInitialized) {
      this.isPageInitialized = true;
      return;
    }

    const pageFilter = this.pageFilterStateService.getFilter();
    if (!pageFilter) {
      this.isPageInitialized = true;
      return;
    }

    // Template Method: subclasses may customizes how filters are applied
    this.applyPageFilterState(pageFilter);

    // Make sure the filter is updated before
    // setting the flag
    setTimeout(() => {
      this.isPageInitialized = true;
    }, 0);
  }

  private initPageRefresh(): void {
    const refreshTime = this.authService.getUserSettings()?.dashboardRefreshTime;
    if (!refreshTime) {
      return;
    }

    this.refreshIntervalId = setInterval(() => {
      if (this.shouldRefresh()) {
        this.onRefresh();
      }
    }, refreshTime * 1000);
  }
}